import { Visibility } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CommonDialog from '../../common/CommonDialog';
import { deleteInventoryApi, getInvcentoryPartDetailAPI } from '../../redux/Inventory/action';
import { INVENTORY_TYPES, PART_TYPES, TableRowsLoader } from '../../utility/Constant';
import { formatCurrency } from '../../utility/helpers/displayHelper';
import PartUsedInvoiceModal from '../Suppliers/SupplierInvoice/PartUsedInvoiceModal';
import AddInventoryModal from './AddInventory';

/**
 * Styles for common UI elements
 */
const styles = {
    // Dialog header styles
    dialogHeader: {
        height: '64px',
        padding: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    },

    // Dialog content styles
    dialogContent: {
        padding: '24px!important',
        maxHeight: '750px',
        overflow: 'auto',
    },

    // Table header cell styles
    tableHeaderCell: {
        padding: '8px 12px',
        height: '40px',
        lineHeight: 'normal',
        fontWeight: 500,
    },

    // Table body cell styles
    tableBodyCell: {
        padding: '7.5px 12px',
        height: '48px !important',
        minHeight: '48px !important',
        maxHeight: '48px !important',
        lineHeight: 1,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
    },

    // Typography for table cell content
    tableCellTypography: {
        fontSize: 14,
        fontWeight: 400,
        letterSpacing: '0.15px',
    },

    // Summary box container style
    summaryBoxContainer: {
        width: '100%',
        borderRadius: '4px',
        overflow: { xs: 'auto', md: 'hidden' },
        mb: 3,
    },

    // Summary box item style
    summaryBoxItem: {
        p: 2,
        borderRight: '1px solid #E0E0E0',
        bgcolor: '#F3F3F3',
        width: '100%',
    },

    // Summary item typography style
    summaryItemTypography: {
        fontFamily: 'Roboto',
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: '150%',
        letterSpacing: '0.15px',
        color: 'rgba(0, 0, 0, 0.87)',
        display: 'flex',
        flexDirection: 'column',
        gap: '0',
    },
};
const BatchViewModal = ({ invoiceId = null, open, onClose, inventoryId, onSave, fetchAPi = () => {} }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    //hooks
    const [partList, setPartList] = useState([]);
    const [rows, setRows] = useState([]);
    const [selecteRecord, setSelectedRecord] = useState([]);
    const [isLoader, setLoader] = useState(false);

    const [showLoader, setShowLoader] = useState(false);
    const [batchNumber, setBatchNumber] = useState(null);
    const [inventoryDetail, setInventoryDetail] = useState(null);
    const [openInventoryModel, setOpenInventoryModal] = useState(false);

    const [customerInvoices, setCustomerInvoices] = useState([]);
    const [partInvoiceModal, setPartInvoiceModal] = useState(false);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Sort parameters for table columns
    const [sortParams, setSortParams] = useState([
        { key: 'supplierInvoice', orderBy: 'DESC' },
        { key: 'qty', orderBy: 'DESC' },
    ]);

    const fetchPartList = useCallback(async () => {
        try {
            setLoader(true);
            const resp = await dispatch(
                getInvcentoryPartDetailAPI({
                    invoiceId: invoiceId,
                    inventory: inventoryId?.inventoryId || inventoryId?.inventory?._id,
                    groupByBatchNumber: true,
                })
            );
            if (resp?.length > 0) {
                setPartList(resp);

                setRows(resp);
            }
        } catch (error) {
        } finally {
            setLoader(false);
        }
    }, [dispatch, inventoryId]);

    useEffect(() => {
        if (open && inventoryId) {
            fetchPartList();
        }
    }, [fetchPartList, open, inventoryId]);

    /**
     * Handles sorting for table columns
     */
    const handleSortRequest = (key) => {
        setSortParams((prevSortParams) => {
            // Find the existing sort index
            const existingSortIndex = prevSortParams.findIndex((param) => param.key === key);

            const newSortParams = [...prevSortParams];

            if (existingSortIndex >= 0) {
                // Toggle the order if the key already exists
                const updatedSort = {
                    ...newSortParams[existingSortIndex],
                    orderBy: newSortParams[existingSortIndex].orderBy === 'ASC' ? 'DESC' : 'ASC',
                };

                // Remove from current position and add to the start
                newSortParams.splice(existingSortIndex, 1);
                newSortParams.unshift(updatedSort);
            } else {
                // Add new sort option to the top
                newSortParams.unshift({ key, orderBy: 'ASC' });
            }

            return newSortParams;
        });
    };

    /**
     * Checks if a column is actively sorted
     * @param {string} key - Column key to check
     * @returns {boolean}
     */
    const isActiveSortColumn = (key) => sortParams.some((param) => param.key === key);

    /**
     * Gets the sort direction for a column
     * @param {string} key - Column key to check
     * @returns {string} - 'asc' or 'desc'
     */
    const getSortDirection = (key) => {
        const param = sortParams.find((param) => param.key === key);
        return param?.orderBy === 'ASC' ? 'asc' : 'desc';
    };

    const handleInputChange = (batchNumber, field, value) => {
        setRows((prevRows) => prevRows.map((row) => (row.batchNumber === batchNumber ? { ...row, [field]: value } : row)));
    };

    /**
     * Renders part summary information
     */
    const renderPartSummary = () => (
        <Box sx={styles.summaryBoxContainer}>
            <Grid container spacing={1.5}>
                {/* Part Number */}
                <Grid item xs={12} sm={4} md={4}>
                    <Box sx={styles.summaryBoxItem}>
                        <Typography sx={styles.summaryItemTypography}>
                            <span style={{ fontWeight: 400 }}>Part #</span>{' '}
                            {rows?.[0]?.inventory?.partNumber || rows?.[0]?.inventory?.tyreModel?.tyreDimension || '-'}
                        </Typography>
                    </Box>
                </Grid>

                {/* Part Name */}
                <Grid item xs={12} sm={4} md={4}>
                    <Box sx={styles.summaryBoxItem}>
                        <Typography sx={styles.summaryItemTypography}>
                            <span style={{ fontWeight: 400 }}>Part Name</span> {rows?.[0]?.inventory?.part?.partName || '-'}
                        </Typography>
                    </Box>
                </Grid>

                {/* Category / Model */}
                <Grid item xs={12} sm={4} md={4}>
                    <Box sx={{ ...styles.summaryBoxItem, borderRight: 'none' }}>
                        <Typography sx={styles.summaryItemTypography}>
                            <span style={{ fontWeight: 500 }}>{rows?.[0]?.inventory?.partsType === PART_TYPES.PARTS ? 'Category' : 'Model'}</span>{' '}
                            {rows?.[0]?.inventory?.partsCategory?.name || rows?.[0]?.inventory?.tyreModel?.name || '-'}
                        </Typography>
                    </Box>
                </Grid>

                {/* Supplier Name */}
                <Grid item xs={12} sm={8} md={8}>
                    <Box sx={styles.summaryBoxItem}>
                        <Typography sx={styles.summaryItemTypography}>
                            <span style={{ fontWeight: 400 }}>Supplier Name</span> {rows?.[0]?.supplierInvoice?.supplier?.name || '-'}
                        </Typography>
                    </Box>
                </Grid>

                {/* Brand */}
                <Grid item xs={12} sm={4} md={4}>
                    <Box sx={styles.summaryBoxItem}>
                        <Typography sx={styles.summaryItemTypography}>
                            <span style={{ fontWeight: 400 }}>Brand</span> {rows?.[0]?.inventory?.partsBrand?.name || '-'}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );

    /**
     * Renders table header cells with sort functionality
     * @param {string} label - Column label
     * @param {string} sortKey - Key for sorting
     * @param {boolean} sortable - Whether column is sortable
     * @returns {React.Component}
     */
    const renderTableHeaderCell = (label, width = '', align = 'left', sortKey = null, sortable = false) => (
        <TableCell sx={{ ...styles.tableHeaderCell }} style={{ width }} align={align}>
            {sortable ? (
                <TableSortLabel active={isActiveSortColumn(sortKey)} direction={getSortDirection(sortKey)} onClick={() => handleSortRequest(sortKey)}>
                    {label}
                </TableSortLabel>
            ) : (
                label
            )}
        </TableCell>
    );

    /**
     * Renders a standard table body cell with typography
     * @param {string|number} content - Cell content
     * @returns {React.Component}
     */
    const renderTableBodyCell = (content, font, align = 'left') => (
        <TableCell sx={styles.tableBodyCell} align={align}>
            <Typography
                sx={{
                    ...styles.tableCellTypography,
                    fontFamily: font ? '16px' : '',
                    fontWeight: font ? 600 : '',
                }}
            >
                {content}
            </Typography>
        </TableCell>
    );
    return (
        <>
            <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="md" fullWidth className="commonDialog__600">
                {/* Dialog Header */}
                <DialogTitle sx={styles.dialogHeader}>
                    <Typography variant="h6">Select which part you want to view</Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon style={{ fill: 'black', fontSize: 32 }} />
                    </IconButton>
                </DialogTitle>

                {/* Dialog Content */}
                <DialogContent sx={styles.dialogContent}>
                    {/* Part Summary Information */}
                    {renderPartSummary()}

                    {/* Part Inventory Table */}
                    <TableContainer
                        component={Paper}
                        sx={{
                            mt: 2,
                            overflow: 'auto',
                            boxShadow: 'none',
                            maxHeight: '250px',
                        }}
                    >
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow sx={{ height: '40px' }}>
                                    {rows?.length > 0 && rows[0]?.isTracked && renderTableHeaderCell('Invoice #', 160)}

                                    {renderTableHeaderCell('Quantity')}
                                    {renderTableHeaderCell('Cost', 147, 'center')}
                                    <TableCell align="right" sx={styles.tableHeaderCell} style={{ width: '150px' }} />
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {isLoader ? (
                                    <TableRowsLoader rowsNum={5} cellsNum={4} />
                                ) : rows && rows.length > 0 ? (
                                    rows.map((row, index) => (
                                        <TableRow
                                            key={row._id}
                                            sx={{ height: '48px', cursor: 'pointer' }}
                                            hover={true}
                                            onClick={() => {
                                                setBatchNumber(row?.batchNumber);
                                                setOpenInventoryModal(true);
                                            }}
                                        >
                                            {/* Invoice number */}
                                            {rows?.length > 0 && rows[0]?.isTracked && renderTableBodyCell(row.supplierInvoice?.invoiceNumber || '-', null)}

                                            {/* Quantity */}
                                            {renderTableBodyCell(
                                                inventoryId?.inventoryBatches?.find((batch) => batch.batchNumber === row.batchNumber)?.qty || 0
                                            )}
                                            {/* Cost */}
                                            {renderTableBodyCell(row.cost ? formatCurrency(row.cost) : '$0', null, 'center')}
                                            {/* Action buttons */}
                                            <TableCell align="right" sx={styles.tableBodyCell}>
                                                <Button startIcon={<Visibility />} variant="contained" color="primary">
                                                    View
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography sx={styles.tableCellTypography}>No Data</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>

                {/* Dialog Actions */}
                {/* <DialogActions style={{ padding: "0 24px 24px 24px" }}>
          <Stack justifyContent={"flex-end"} direction={"row"} gap={1.5}>
            <Button color="primary" variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                onSave(rows);
              }}
            >
              Apply to invoice
            </Button>
          </Stack>
        </DialogActions> */}
            </Dialog>

            <AddInventoryModal
                viewOnly={true}
                type={INVENTORY_TYPES.INVENTORY}
                inventoryId={inventoryId}
                batchNumber={batchNumber}
                openModal={openInventoryModel}
                closeModal={() => {
                    setOpenInventoryModal(false);
                    setBatchNumber(null);
                }}
                onSubmitModal={() => {
                    fetchPartList();
                    setOpenInventoryModal(false);
                    setBatchNumber(null);
                }}
                hideTabs
                setShowLoader={setShowLoader}
                showLoader={showLoader}
                fetchApi={(data) => {
                    if (data) {
                        fetchPartList();
                    }
                }}
                ShowAllTabs={false}
                ShowTabIndex={0}
                modalType="add"
            />

            <PartUsedInvoiceModal
                open={partInvoiceModal}
                onClose={() => {
                    setPartInvoiceModal(false);
                }}
                invoiceData={customerInvoices}
            />

            <CommonDialog
                loader={isDeleting}
                open={showDeleteModal}
                onClose={() => {
                    setInventoryDetail(null);
                    setBatchNumber(null);
                    setShowDeleteModal(false);
                }}
                onSave={async () => {
                    let resp;

                    if (inventoryDetail?.isUsedInCustomerInvoice) {
                        setPartInvoiceModal(true);
                        return;
                    }
                    setIsDeleting(true);
                    if (!inventoryDetail?.isTracked) {
                        resp = await deleteInventoryApi(inventoryDetail?.inventory?._id, inventoryDetail?.batchNumber);
                    } else if (inventoryDetail?.supplierInvoice?._id) {
                        navigate(
                            `/suppliers-invoice/${
                                inventoryDetail?.supplierInvoice?.paymentStatus === 'open'
                                    ? 'open'
                                    : inventoryDetail?.supplierInvoice?.paymentStatus === 'partial_payment'
                                      ? 'partial'
                                      : 'paid'
                            }/` + inventoryDetail.supplierInvoice._id
                        );
                    }
                    if (resp) {
                        setInventoryDetail(null);
                        setBatchNumber(null);
                        setShowDeleteModal(false);
                        fetchPartList();
                    }
                    setIsDeleting(false);
                }}
                dialogClaassName={inventoryDetail?.isTracked ? 'commonDialog commonDialog__600' : 'commonDialog '}
                dialogTitle={
                    inventoryDetail?.isUsedInCustomerInvoice
                        ? '🚫 Cannot Delete Inventory'
                        : !inventoryDetail?.isTracked
                          ? 'Delete Part?'
                          : '⚠️ Part added from Supplier Invoice'
                }
                dialogSubtitle={
                    inventoryDetail?.isUsedInCustomerInvoice
                        ? 'This part has been used in at least one customer invoice.'
                        : !inventoryDetail?.isTracked
                          ? 'Are you sure you want to delete this part from your inventory?'
                          : 'This part was originally added through a Supplier Invoice.'
                }
                dialogSubtitle2={
                    inventoryDetail?.isUsedInCustomerInvoice
                        ? 'To preserve invoice history, deletion is not allowed. To hide this part, click View In-Stock Parts Only.'
                        : !inventoryDetail?.isTracked
                          ? ''
                          : 'To maintain accurate inventory and costing, please delete it directly from the Supplier Invoice.'
                }
                saveButtonText={
                    inventoryDetail?.isUsedInCustomerInvoice
                        ? `view Invoice${customerInvoices.length > 1 ? 's' : ''}`
                        : !inventoryDetail?.isTracked
                          ? 'Delete'
                          : 'view supplier Invoice'
                }
                saveButtonType={inventoryDetail?.isUsedInCustomerInvoice ? 'primary' : !inventoryDetail?.isTracked ? 'error' : 'primary'}
                saveButtonWidth={inventoryDetail?.isUsedInCustomerInvoice ? '148px' : !inventoryDetail?.isTracked ? '103px' : '210px'}
            />
        </>
    );
};

export default BatchViewModal;
